// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import './SectionHeader.css';

export interface SectionHeaderProps {
  title: string;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  type: 'relevant' | 'not_relevant' | 'not_analyzed';
  ariaControls?: string;
}

/**
 * SectionHeader component for binary section structure in Evidence Drawer
 * Displays collapsible header with visual cues (green for relevant, grey for not relevant)
 * Accessibility: Provides aria-expanded, aria-label, and keyboard navigation support
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  count,
  isExpanded,
  onToggle,
  type,
  ariaControls,
}) => {
  const colorClass =
    type === 'relevant'
      ? 'evidence-drawer__section-header--relevant'
      : type === 'not_analyzed'
      ? 'evidence-drawer__section-header--not-analyzed'
      : 'evidence-drawer__section-header--not-relevant';

  const dotIcon = type === 'relevant' ? '●' : '●';

  // Keyboard navigation: Enter and Space toggle expansion
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  // Accessibility label for screen readers
  const ariaLabel = `${title} (${count} papers), ${isExpanded ? 'expanded' : 'collapsed'}. Press Enter to toggle`;

  return (
    <button
      className={`evidence-drawer__section-header ${colorClass}`}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      aria-expanded={isExpanded}
      aria-label={ariaLabel}
      aria-controls={ariaControls}
      type="button"
    >
      <div className="evidence-drawer__section-header-content">
        <span className={`evidence-drawer__section-dot evidence-drawer__section-dot--${
          type === 'relevant' ? 'relevant' :
          type === 'not_analyzed' ? 'not-analyzed' :
          'not-relevant'
        }`}>
          {dotIcon}
        </span>
        <span className="evidence-drawer__section-title">
          {title}
        </span>
        <span className="evidence-drawer__section-count">
          ({count})
        </span>
      </div>
      <div className={`evidence-drawer__section-chevron ${isExpanded ? 'evidence-drawer__section-chevron--expanded' : ''}`}>
        ▼
      </div>
    </button>
  );
};
